import { LitElement, css, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import '@shoelace-style/shoelace/dist/components/card/card.js';
import '@shoelace-style/shoelace/dist/components/input/input.js';
import '@shoelace-style/shoelace/dist/components/button/button.js';
import '@shoelace-style/shoelace/dist/components/alert/alert.js';
import '@shoelace-style/shoelace/dist/components/badge/badge.js';
import '@shoelace-style/shoelace/dist/components/icon/icon.js';
import { ApiFactory } from '../api/easypos-api';
import { resolveRouterPath, router } from '../router';
import { write } from '../language/lang';
import { styles as sharedStyles } from '../styles/shared-styles';

// Mirrors the server-side slug rule (Server/EasyposServer/Entities/System/
// ShopAccountRegister.cs and the SHOP_SLUG regex on shop-create-page).
const SHOP_SLUG = /^[a-z0-9](?:[a-z0-9-]{1,30}[a-z0-9])?$/;

// "Try with sample data" sandbox creation page. Lets the operator name
// a shop, then POSTs /api/shop/sample which creates the shop AND seeds
// it (locations / suppliers / stock / categories / menu / publish) so
// the operator lands in a working shop ready to click around.
//
// Sample shops are rate-limited server-side to 3 paid sales per UTC day
// to block real operational use — the operator can play with the
// register and reports, but volume use forces an upgrade. This page
// surfaces both rules in copy so there's no surprise.
type SampleArchetype = 'cafe' | 'clothing' | 'sarisari';

interface ArchetypeOption {
  id: SampleArchetype;
  label: string;
  icon: string;
  defaultDisplayName: string;
  blurb: string;
  highlights: string[];
}

const ARCHETYPES: ArchetypeOption[] = [
  {
    id: 'cafe',
    label: 'Cafe',
    icon: 'cup-hot',
    defaultDisplayName: 'Sample Cafe',
    blurb:
      'A recipe-driven menu — modifiers, sizes, milk swaps, a combo. The "made-to-order" shape.',
    highlights: [
      '13 stock items (beans, milks, syrups, bakery)',
      '12 menu items including a combo',
      'Size scaling + quantifiable extras',
    ],
  },
  {
    id: 'clothing',
    label: 'Clothing store',
    icon: 'bag',
    defaultDisplayName: 'Sample Clothing Store',
    blurb:
      'Retail apparel — one product per colour, each with size variants and per-cell barcodes.',
    highlights: [
      'Pag-asenso Tee Navy / Red (S / M / L)',
      'Hoodie, cap, tote, tumblers',
      'Per-cell scannable barcodes',
    ],
  },
  {
    id: 'sarisari',
    label: 'Sari-sari store',
    icon: 'shop-window',
    defaultDisplayName: 'Sample Sari-Sari Store',
    blurb:
      'Neighborhood convenience store — flat scannable SKUs, no variants, no modifiers.',
    highlights: [
      '12+ products across snacks, drinks, sachets, toiletries',
      'One barcode per item for scan-to-sell',
      'No recipes — sell straight on price',
    ],
  },
];

@customElement('shop-sample-page')
export class ShopSamplePage extends LitElement {
  @state() private _shopName = '';
  @state() private _displayName = '';
  @state() private _archetype: SampleArchetype = 'cafe';
  @state() private _busy = false;
  @state() private _error: string | null = null;

  static styles = [
    sharedStyles,
    css`
      :host {
        display: block;
        max-width: 560px;
        margin: 0 auto;
        padding: 16px;
        box-sizing: border-box;
      }
      h2 {
        margin: 0;
        font-family: var(--font-display);
        font-weight: 700;
        font-size: 1.4rem;
        letter-spacing: -0.02em;
      }
      .lede {
        margin: 0;
        color: var(--app-text-muted, var(--sl-color-neutral-600));
        font-size: 0.9rem;
      }
      form {
        display: flex;
        flex-direction: column;
        gap: 14px;
      }
      .seed-summary {
        display: flex;
        flex-direction: column;
        gap: 6px;
        padding: 12px 14px;
        background: var(--sl-color-neutral-100);
        border-radius: var(--sl-border-radius-medium);
        font-size: 0.88rem;
      }
      .sl-theme-dark .seed-summary {
        background: var(--sl-color-neutral-800);
      }
      .seed-summary strong {
        font-family: var(--font-display);
      }
      .seed-summary ul {
        margin: 4px 0 0 0;
        padding-left: 18px;
      }
      .limit-callout {
        display: flex;
        align-items: flex-start;
        gap: 10px;
        padding: 10px 12px;
        border-radius: var(--sl-border-radius-medium);
        background: var(--sl-color-warning-50);
        border-left: 3px solid var(--sl-color-warning-600);
        font-size: 0.85rem;
      }
      .sl-theme-dark .limit-callout {
        background: rgba(245, 158, 11, 0.08);
      }
      .limit-callout sl-icon {
        font-size: 1.1rem;
        color: var(--sl-color-warning-700);
        flex-shrink: 0;
        margin-top: 2px;
      }
      .name-hint {
        font-size: 0.78rem;
        color: var(--app-text-muted, var(--sl-color-neutral-600));
      }
      .name-hint code {
        font-family: var(--font-mono);
        font-size: 0.82em;
      }
      .actions {
        display: flex;
        gap: 8px;
        justify-content: space-between;
        align-items: center;
      }
      /* Archetype picker — three tiles on wider screens, stacks on phone. */
      .archetype-grid {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: 8px;
      }
      @media (max-width: 600px) {
        .archetype-grid {
          grid-template-columns: 1fr;
        }
      }
      .archetype-tile {
        appearance: none;
        font: inherit;
        text-align: left;
        cursor: pointer;
        background: var(--sl-color-neutral-0);
        border: 1.5px solid var(--sl-color-neutral-200);
        border-radius: var(--sl-border-radius-large);
        padding: 12px;
        display: flex;
        flex-direction: column;
        gap: 6px;
        color: inherit;
        transition:
          border-color 80ms ease,
          transform 80ms ease,
          box-shadow 80ms ease;
      }
      .archetype-tile:hover {
        border-color: var(--sl-color-neutral-400);
      }
      .archetype-tile.selected {
        border-color: var(--brand-rose-600, var(--sl-color-primary-600));
        box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.04);
      }
      .archetype-tile sl-icon {
        font-size: 1.2rem;
        color: var(--brand-rose-600, var(--sl-color-primary-600));
      }
      .archetype-tile strong {
        font-size: 0.95rem;
      }
      .archetype-tile p {
        margin: 0;
        font-size: 0.8rem;
        color: var(--app-text-muted, var(--sl-color-neutral-600));
        line-height: 1.35;
      }
    `,
  ];

  private _onInput(field: 'shopName' | 'displayName') {
    return (e: Event) => {
      const value = (e.target as HTMLInputElement).value;
      if (field === 'shopName') this._shopName = value.toLowerCase().trim();
      if (field === 'displayName') this._displayName = value;
    };
  }

  private async _submit(e: Event) {
    e.preventDefault();
    if (this._busy) return;
    this._error = null;

    const shopName = this._shopName.trim();
    const displayName = this._displayName.trim();
    if (!shopName || !displayName) {
      this._error = 'Pick a shop URL slug and a display name to continue.';
      return;
    }
    if (!SHOP_SLUG.test(shopName)) {
      this._error =
        'Shop URL must be 3–32 chars, lowercase letters, numbers, hyphens. Cannot start or end with a hyphen.';
      return;
    }

    this._busy = true;
    try {
      const api = ApiFactory.authenticated();
      const r = await api.createSampleShop({
        shopName,
        displayName,
        timezone: browserTimezone() || 'UTC',
        archetype: this._archetype,
      });
      if (!r.success || !r.data) {
        this._error = r.error ?? 'Could not create the sample shop.';
        return;
      }
      // Token already on the API client. Tell the app shell to refresh
      // user-profile + select the new shop, then go home.
      window.dispatchEvent(
        new CustomEvent('shop-created', {
          detail: { token: r.data.token, shop: r.data.shop },
        })
      );
      router.navigate(resolveRouterPath(''));
    } catch (err) {
      this._error = (err as Error)?.message ?? 'Unexpected error.';
    } finally {
      this._busy = false;
    }
  }

  private _cancel() {
    router.navigate(resolveRouterPath('shop/select'));
  }

  render() {
    return html`
      <sl-card>
        <div
          slot="header"
          style="display: flex; align-items: center; gap: 8px;"
        >
          <h2>${write`${'ref:shop:sample-title'}Try with sample data`}</h2>
          <sl-badge variant="primary" pill>SAMPLE</sl-badge>
        </div>

        <form @submit=${this._submit}>
          <p class="lede">
            A pre-loaded shop you can poke around in — pick the kind that
            matches what you're testing.
          </p>

          <div
            class="archetype-grid"
            role="radiogroup"
            aria-label="Shop archetype"
          >
            ${ARCHETYPES.map((a) => {
              const selected = this._archetype === a.id;
              return html`
                <button
                  type="button"
                  class="archetype-tile ${selected ? 'selected' : ''}"
                  role="radio"
                  aria-checked=${selected ? 'true' : 'false'}
                  @click=${() => {
                    this._archetype = a.id;
                    if (!this._displayName.trim())
                      this._displayName = a.defaultDisplayName;
                  }}
                >
                  <sl-icon name=${a.icon}></sl-icon>
                  <strong>${a.label}</strong>
                  <p>${a.blurb}</p>
                </button>
              `;
            })}
          </div>

          <div class="seed-summary">
            <strong>Pre-loaded for you:</strong>
            <ul>
              ${(
                ARCHETYPES.find((a) => a.id === this._archetype) ??
                ARCHETYPES[0]
              ).highlights.map((h) => html`<li>${h}</li>`)}
              <li>Menu published — ready to sell</li>
            </ul>
          </div>

          <sl-input
            label=${write`${'ref:shop:slug'}Shop URL slug`}
            .value=${this._shopName}
            @sl-input=${this._onInput('shopName')}
            autocomplete="off"
            spellcheck="false"
            required
          ></sl-input>
          <p class="name-hint">
            Used in URLs and storage — must be globally unique across all
            EasyPos shops. Lowercase letters, numbers, hyphens (e.g.
            <code
              >${
                this._displayName
                  ? slugify(this._displayName)
                  : 'my-cafe-sandbox'
              }</code
            >). If it's taken you'll get an error and can try another.
          </p>

          <sl-input
            label=${write`${'ref:shop:display-name'}Display name`}
            help-text="The name you'll see at the top of the shop."
            .value=${this._displayName}
            @sl-input=${this._onInput('displayName')}
            required
          ></sl-input>

          <div class="limit-callout">
            <sl-icon name="exclamation-triangle"></sl-icon>
            <div>
              <strong>3 sales per day limit.</strong>
              Sample shops are for exploring — not running a real business. You
              can ring up the first 3 sales each day; the 4th is blocked.
              Upgrade to <strong>Bronze</strong> or higher for unlimited sales.
            </div>
          </div>

          ${
            this._error
              ? html`<sl-alert
                  variant="danger"
                  open
                  closable
                  @sl-after-hide=${() => (this._error = null)}
                >
                  ${this._error}
                </sl-alert>`
              : nothing
          }

          <div class="actions">
            <sl-button
              variant="default"
              @click=${this._cancel}
              ?disabled=${this._busy}
            >
              Cancel
            </sl-button>
            <sl-button
              type="submit"
              variant="primary"
              ?loading=${this._busy}
              ?disabled=${this._busy}
            >
              <sl-icon slot="prefix" name="cup-hot"></sl-icon>
              ${write`${'ref:shop:sample-submit'}Create sample shop`}
            </sl-button>
          </div>
        </form>
      </sl-card>
    `;
  }
}

function browserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

// Permissive client-side slug suggestion. The server is the authority on
// the actual slug rule; this just gives the operator a sensible default
// to type over.
function slugify(s: string): string {
  return (
    s
      .toLowerCase()
      .replace(/['"’]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 32) || 'my-shop'
  );
}
